import { Component, ChangeDetectionStrategy, inject, computed, signal } from '@angular/core';
import {
  RouterOutlet,
  Router,
  NavigationEnd,
  NavigationStart,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { filter } from 'rxjs';
import { ShellComponent } from '@layouts/shell/shell.component';
import { LoadingOverlayComponent } from '@shared/components/loading-overlay/loading-overlay.component';
import { AuthService } from '@core/services/auth.service';
import { LoadingService } from '@core/services/loading.service';
import { isInternalRole } from '@core/models/user.model';
import { IssueReportButtonComponent } from '@shared/components/issue-report/issue-report-button.component';
import { WELLS_NPT_ENABLED, WellsContextService } from '@core/services/wells-context.service';
import { WellsSupplierContextService } from '@core/services/wells-supplier-context.service';

@Component({
  selector: 'qms-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, ShellComponent, LoadingOverlayComponent, IssueReportButtonComponent],
  template: `
    @if (showShell()) {
      <qms-shell>
        <router-outlet />
      </qms-shell>
    } @else {
      <router-outlet />
    }
    <qms-loading-overlay />
    @if (showIssueReport()) { <qms-issue-report-button /> }
  `,
})
export class AppComponent {
  private auth = inject(AuthService);
  private router = inject(Router);
  private loading = inject(LoadingService);
  private readonly wellsContext = inject(WellsContextService);
  private readonly supplierContext = inject(WELLS_NPT_ENABLED) ? inject(WellsSupplierContextService) : null;

  /** Release function for the navigation currently being tracked by the busy overlay. */
  private navigationDone: (() => void) | null = null;
  private readonly currentUrl = signal(this.router.url);
  readonly showIssueReport = computed(() => !this.supplierContext?.restricted() && !this.wellsContext.active() && !!this.auth.currentUser() && this.currentUrl().split('?')[0] !== '/request-access');

  constructor() {
    // Record user activity (LastSeenAt) on every navigation; the service throttles the API call.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.auth.recordHeartbeat();
      });

    // Route changes pull down lazy-loaded chunks and run resolvers, which can take a noticeable
    // moment on a cold cache with nothing on screen to show for it — track them too.
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigationDone ??= this.loading.begin();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.navigationDone?.();
        this.navigationDone = null;
      }
    });
  }

  readonly showShell = computed(() => {
    if (this.currentUrl().split('?')[0] === '/request-access') return false;
    const user = this.auth.currentUser();
    if (!user) return false;
    if (this.supplierContext?.restricted() || this.wellsContext.active()) return true;
    // Use activeRole to determine shell: if admin switched to external role, hide internal shell
    const active = this.auth.activeRole();
    const effectiveRole = active ?? user.role;
    return isInternalRole(effectiveRole);
  });
}
